/**
 * /tasks/:taskId/punch — supervisor's punch list management screen.
 *
 * Lists punch items for a punch task. Filter by status, add new items
 * (with zone tag), advance lifecycle (open → assigned → in_progress →
 * closed), mark blocks_recap. Items that block_recap appear with a red
 * pill so it's obvious which ones are gating the project closeout.
 *
 * Floorplan-pinning + photo evidence ship in M8.2 once the multipart
 * upload pipeline lands.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  CircleSlash,
  Loader2,
  Map as MapIcon,
  Plus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import type { PunchItem, PunchItemStatus } from '@vari-crew/schemas';
import { PunchItemStatusSchema } from '@vari-crew/schemas';
import {
  useClosePunchItem,
  useCreatePunchItem,
  useDeletePunchItem,
  usePunchItems,
  useUpdatePunchItem,
} from '@/api/punch-items';
import { useTask } from '@/api/tasks';
import { useProject } from '@/api/projects';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/Dialog';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { PhotoStrip } from '@/components/punch/PhotoStrip';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/lib/format';

const STATUS_CLASS: Record<PunchItemStatus, string> = {
  open: 'bg-status-scheduled text-status-scheduled-fg',
  assigned: 'bg-status-enroute text-status-enroute-fg',
  in_progress: 'bg-status-onsite text-status-onsite-fg',
  closed: 'bg-status-complete text-status-complete-fg',
};

const STATUS_LABEL: Record<PunchItemStatus, string> = {
  open: 'Open',
  assigned: 'Assigned',
  in_progress: 'In progress',
  closed: 'Closed',
};

export function PunchList() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { data: task } = useTask(taskId);
  const { data: project } = useProject(task?.project_id);
  const { data: items, isLoading, error, refetch } = usePunchItems(taskId);
  const createItem = useCreatePunchItem(taskId ?? '');
  const updateItem = useUpdatePunchItem(taskId ?? '');
  const closeItem = useClosePunchItem(taskId ?? '');
  const deleteItem = useDeletePunchItem(taskId ?? '');

  const [filter, setFilter] = useState<PunchItemStatus | 'all'>('all');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PunchItem | null>(null);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (filter === 'all') return items.data;
    return items.data.filter((i) => i.status === filter);
  }, [items, filter]);

  const counts = useMemo(() => {
    const base = {
      total: 0,
      open: 0,
      assigned: 0,
      in_progress: 0,
      closed: 0,
      blocking: 0,
    };
    if (!items) return base;
    for (const i of items.data) {
      base.total += 1;
      base[i.status] += 1;
      if (i.blocks_recap && i.status !== 'closed') base.blocking += 1;
    }
    return base;
  }, [items]);

  if (isLoading) {
    return (
      <div className="p-6">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }

  if (error) {
    // Distinct from "No punch items yet." — an errored list must not read
    // as a clean punch list.
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-3" role="alert">
        <div className="text-sm text-status-blocked-fg">
          Couldn&apos;t load punch items ({error.message}).
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <header className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="text-xs text-ink-faint">
            {project ? (
              <Link to={`/projects/${project.id}`} className="hover:underline">
                {project.title}
              </Link>
            ) : (
              'Project'
            )}{' '}
            ·{' '}
            <span className="font-mono">
              {task?.title ?? 'Punch list'}
            </span>
          </div>
          <h1 className="text-2xl font-semibold">Punch list</h1>
          <p className="text-sm text-ink-dim">
            {counts.total} item{counts.total === 1 ? '' : 's'} ·{' '}
            {counts.total - counts.closed} open · {counts.closed} closed
            {counts.blocking > 0 && (
              <span className="ml-1 text-status-blocked-fg font-medium">
                · {counts.blocking} blocking recap
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to={`/tasks/${taskId}/floorplan`}>
              <MapIcon className="w-3.5 h-3.5" />
              Floorplan
            </Link>
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="w-3.5 h-3.5" />
            Add item
          </Button>
        </div>
      </header>

      {/* Filter chips */}
      <div className="flex gap-1 flex-wrap">
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
          All {counts?.total ?? 0}
        </FilterChip>
        {PunchItemStatusSchema.options.map((s) => (
          <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)}>
            {STATUS_LABEL[s]} {counts?.[s] ?? 0}
          </FilterChip>
        ))}
      </div>

      {creating && (
        <CreateItemCard
          onCancel={() => setCreating(false)}
          onSubmit={async (values) => {
            try {
              await createItem.mutateAsync(values);
              toast.success('Punch item added');
              setCreating(false);
            } catch (err) {
              toast.error(`Failed: ${(err as Error).message}`);
            }
          }}
          pending={createItem.isPending}
        />
      )}

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-ink-dim">
            {filter === 'all'
              ? 'No punch items yet. Add one to get started.'
              : `No punch items with status "${STATUS_LABEL[filter as PunchItemStatus]}".`}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              onAdvance={(to) =>
                updateItem.mutate(
                  { id: item.id, body: { status: to } },
                  {
                    onSuccess: () => toast.success(`Marked ${STATUS_LABEL[to]}`),
                    onError: (e) => toast.error(`Failed: ${e.message}`),
                  },
                )
              }
              onClose={() =>
                closeItem.mutate(
                  { id: item.id },
                  {
                    onSuccess: () => toast.success('Closed'),
                    onError: (e) => toast.error(`Failed: ${e.message}`),
                  },
                )
              }
              onDelete={() => setDeleteTarget(item)}
              onToggleBlocking={() =>
                updateItem.mutate({
                  id: item.id,
                  body: { blocks_recap: !item.blocks_recap },
                })
              }
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteItem.mutate(deleteTarget.id, {
            onSuccess: () => toast.success('Deleted'),
            onError: (e) => toast.error(`Failed: ${e.message}`),
            onSettled: () => setDeleteTarget(null),
          });
        }}
        title="Delete punch item?"
        body={
          deleteTarget
            ? `"${deleteTarget.title}" and its photo evidence will be permanently removed. Closing the item instead keeps the record.`
            : ''
        }
        confirmLabel="Delete"
        tone="danger"
        busy={deleteItem.isPending}
      />
    </div>
  );
}

function ItemRow({
  item,
  onAdvance,
  onClose,
  onDelete,
  onToggleBlocking,
}: {
  item: PunchItem;
  onAdvance: (to: PunchItemStatus) => void;
  onClose: () => void;
  onDelete: () => void;
  onToggleBlocking: () => void;
}) {
  const isClosed = item.status === 'closed';
  return (
    <Card className={cn(isClosed && 'opacity-60')}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  STATUS_CLASS[item.status],
                )}
              >
                {STATUS_LABEL[item.status]}
              </span>
              {item.blocks_recap && !isClosed && (
                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-status-blocked text-status-blocked-fg">
                  <AlertTriangle className="w-2.5 h-2.5" /> blocks recap
                </span>
              )}
              {item.zone_tag && (
                <span className="font-mono text-[10px] text-ink-faint">
                  {item.zone_tag}
                </span>
              )}
            </div>
            <div className={cn('font-medium mt-1', isClosed && 'line-through')}>
              {item.title}
            </div>
            {item.description && (
              <div className="text-xs text-ink-dim mt-1 whitespace-pre-wrap">
                {item.description}
              </div>
            )}
            <div className="text-[10px] text-ink-faint mt-2">
              {isClosed && item.closed_at
                ? `Closed ${formatRelative(item.closed_at)}`
                : `Updated ${formatRelative(item.updated_at)}`}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {!isClosed && item.status !== 'in_progress' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  onAdvance(item.status === 'open' ? 'assigned' : 'in_progress')
                }
                title="Advance status"
              >
                {item.status === 'open' ? 'Assign' : 'Start'}
              </Button>
            )}
            {!isClosed && (
              <Button size="sm" onClick={onClose} title="Close">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Close
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleBlocking}
              title={item.blocks_recap ? 'Unmark as blocking recap' : 'Mark as blocking recap'}
            >
              {item.blocks_recap ? (
                <AlertTriangle className="w-3.5 h-3.5 text-status-blocked-fg" />
              ) : (
                <CircleSlash className="w-3.5 h-3.5 text-ink-faint" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              title="Delete"
              className="text-status-blocked-fg"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <PhotoStrip punchItemId={item.id} />
      </CardContent>
    </Card>
  );
}

function CreateItemCard({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (values: {
    title: string;
    description?: string | null;
    zone_tag?: string | null;
    blocks_recap: boolean;
  }) => void;
  onCancel: () => void;
  pending: boolean;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [zoneTag, setZoneTag] = useState('');
  const [blocksRecap, setBlocksRecap] = useState(false);
  return (
    <Card>
      <CardHeader>
        <CardTitle>New punch item</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!title.trim()) return;
            onSubmit({
              title: title.trim(),
              description: description.trim() || null,
              zone_tag: zoneTag.trim() || null,
              blocks_recap: blocksRecap,
            });
          }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="punch-title">Title</Label>
            <Input
              id="punch-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Workstation 3 — drawer pull off-center"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="punch-desc">Description (optional)</Label>
            <Textarea
              id="punch-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What needs to happen?"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="punch-zone">Zone tag (optional)</Label>
              <Input
                id="punch-zone"
                value={zoneTag}
                onChange={(e) => setZoneTag(e.target.value)}
                placeholder="e.g. L3 · WS3"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={blocksRecap}
                  onChange={(e) => setBlocksRecap(e.target.checked)}
                />
                Blocks recap
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending || !title.trim()}>
              {pending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Add item
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 text-xs rounded-md border transition-colors',
        active
          ? 'border-ink bg-ink text-bg'
          : 'border-line bg-bg text-ink-dim hover:bg-fill-soft',
      )}
    >
      {children}
    </button>
  );
}
